/**
 * promesa-variant-selector.js  v2.1
 * Promesa Joyería — Selector de variantes unificado
 * Tema: Showcase (Clickybox + pretty-select) | Shopify
 *
 * Arquitectura del tema (detectada via promesaDiscovery):
 *   - Metal:    <div data-option-name="Metal"> + <select class="noreplace clickybox-replaced">
 *   - Piedra:   <div class="pretty-select"> + <select aria-label="Piedra">
 *   - Quilates: <div class="pretty-select"> + <select aria-label="Quilates">
 *
 * v2.1 — FIX: los quilates válidos por piedra ya NO están codificados a mano
 * (esa lista solo coincidía con las medidas del corte Redondo y dejaba el
 * selector de Quilates congelado — todas las opciones deshabilitadas — en
 * cualquier otro corte, como Oval, donde moissanita y diamante lab tienen
 * medidas distintas al mismo quilataje). Ahora se derivan en tiempo real
 * desde /products/{handle}.js (endpoint AJAX propio de Shopify), leyendo
 * las variantes reales del producto. Se conserva la lista vieja como
 * QUILATES_FALLBACK únicamente por si el fetch llegara a fallar.
 */

(function () {
  'use strict';

  // ═══════════════════════════════════════════════════════════════════
  // A. VALORES — exactos según el DOM de Shopify
  //    (sensibles a mayúsculas y espacios)
  // ═══════════════════════════════════════════════════════════════════

  var PIEDRA = {
    MOI: 'Moissanita',
    DIA: 'Diamante lab',   // 'l' minúscula — tal como está guardado en Shopify
  };

  var METAL_BLOQUEADO = 'Plata (blanco)';

  // Fallback — solo se usa si falla el fetch a /products/{handle}.js.
  // Corresponde a las medidas de corte Redondo; no es válido para otros cortes.
  var QUILATES_FALLBACK = {
    'Moissanita': new Set([
      '0.5 (5.0mm)', '0.6 (5.5mm)', '0.8 (6.0mm)', '1.0 (6.5mm)',
      '1.2 (7.0mm)', '1.5 (7.5mm)', '2.0 (8.0mm)', '2.5 (8.5mm)',
      '3.0 (9.0mm)', '3.5 (9.5mm)', '4.0 (10.0mm)', '4.5 (10.5mm)',
      '5.0 (11.0mm)',
    ]),
    'Diamante lab': new Set([
      '0.5 (5.0mm)', '0.6 (5.5mm)', '0.7 (5.8mm)', '0.8 (6.0mm)',
      '0.9 (6.3mm)', '1.0 (6.5mm)', '1.1 (6.8mm)', '1.2 (7.0mm)',
      '1.3 (7.2mm)', '1.4 (7.4mm)', '1.5 (7.5mm)', '1.8 (7.8mm)',
      '2.0 (8.0mm)', '2.5 (8.5mm)', '3.0 (9.0mm)',
    ]),
  };

  // Se rellena dinámicamente en init() a partir de las variantes reales
  // del producto. Mientras no esté listo, filterQuilates usa el fallback.
  var QUILATES_BY_PIEDRA = null;

  // ═══════════════════════════════════════════════════════════════════
  // A2. DESCUBRIR QUILATES VÁLIDOS POR PIEDRA — vía AJAX API de Shopify
  //     GET /products/{handle}.js devuelve el producto completo, con
  //     todas sus variantes reales (option1/2/3 según posición de cada
  //     opción). Construimos, por cada valor de Piedra, el conjunto de
  //     valores de Quilates que realmente existen como variante.
  // ═══════════════════════════════════════════════════════════════════

  function fetchQuilatesPorPiedra(cb) {
    var path = window.location.pathname.replace(/\/$/, '');
    // Quita cualquier sufijo .js previo por seguridad y vuelve a añadirlo
    path = path.replace(/\.js$/, '') + '.js';

    fetch(path, { credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .then(function (data) {
        if (!data || !Array.isArray(data.variants) || !Array.isArray(data.options)) {
          cb(null);
          return;
        }

        var piedraOpt   = data.options.find(function (o) { return o.name === 'Piedra'; });
        var quilatesOpt = data.options.find(function (o) { return o.name === 'Quilates'; });
        if (!piedraOpt || !quilatesOpt) { cb(null); return; }

        var piedraKey   = 'option' + piedraOpt.position;
        var quilatesKey = 'option' + quilatesOpt.position;

        var map = {};
        data.variants.forEach(function (v) {
          var piedra   = v[piedraKey];
          var quilate  = v[quilatesKey];
          if (!piedra || !quilate) return;
          if (!map[piedra]) map[piedra] = new Set();
          map[piedra].add(quilate);
        });

        // Si no se pudo derivar nada útil, deja que se use el fallback
        cb(Object.keys(map).length ? map : null);
      })
      .catch(function () { cb(null); });
  }

  // ═══════════════════════════════════════════════════════════════════
  // B. GETTERS — selectores exactos para el tema Showcase
  // ═══════════════════════════════════════════════════════════════════

  function getPiedraSelect()   { return document.querySelector('select[aria-label="Piedra"]'); }
  function getQuilatesSelect() { return document.querySelector('select[aria-label="Quilates"]'); }
  function getMetalSelect() {
    var w = document.querySelector('[data-option-name="Metal"]');
    return w ? w.querySelector('select') : null;
  }

  // ═══════════════════════════════════════════════════════════════════
  // C. TRIGGER DE CAMBIO DE VARIANTE
  //    Notifica al tema que el select fue modificado por código
  //    para que actualice precio, imagen y variante seleccionada
  // ═══════════════════════════════════════════════════════════════════

  function triggerChange(el) {
    el.dispatchEvent(new Event('change', { bubbles: true }));
    el.dispatchEvent(new Event('input',  { bubbles: true }));
  }

  // ═══════════════════════════════════════════════════════════════════
  // D. ACTUALIZAR DISPLAY DEL PRETTY-SELECT
  //    El pretty-select muestra el valor actual en <span class="value">
  //    Es necesario actualizarlo a mano cuando cambiamos el select por código
  // ═══════════════════════════════════════════════════════════════════

  function refreshPrettyDisplay(selectEl) {
    var wrapper = selectEl.closest('.pretty-select');
    if (!wrapper) return;
    var valueSpan = wrapper.querySelector('.text .value, .value');
    if (valueSpan) valueSpan.textContent = selectEl.value;
  }

  // ═══════════════════════════════════════════════════════════════════
  // E. FILTRAR QUILATES SEGÚN PIEDRA ACTIVA
  // ═══════════════════════════════════════════════════════════════════

  function filterQuilates(piedra) {
    var sel = getQuilatesSelect();
    if (!sel) return;

    var fuente = QUILATES_BY_PIEDRA || QUILATES_FALLBACK;
    var valid = fuente[piedra] || new Set();
    var currentIsValid = valid.has(sel.value);
    var firstValid = null;

    Array.from(sel.options).forEach(function (opt) {
      var ok = valid.has(opt.value);
      opt.disabled     = !ok;
      opt.style.display = ok ? '' : 'none';
      if (ok && !firstValid) firstValid = opt.value;
    });

    // Si el quilate seleccionado no existe para la nueva piedra → resetear al primero válido
    if (!currentIsValid && firstValid) {
      sel.value = firstValid;
      refreshPrettyDisplay(sel);
      triggerChange(sel);
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // F. PLATA — deshabilitar / habilitar según piedra
  // ═══════════════════════════════════════════════════════════════════

  function setPlataDisabled(disabled) {
    var sel = getMetalSelect();
    if (!sel) return;

    // 1. Opción nativa del select
    var opt = Array.from(sel.options).find(function (o) { return o.value === METAL_BLOQUEADO; });
    if (opt) {
      opt.disabled = disabled;
      // Si Plata estaba seleccionada y se deshabilita, cambiar al siguiente metal válido
      if (disabled && sel.value === METAL_BLOQUEADO) {
        var next = Array.from(sel.options).find(function (o) { return !o.disabled; });
        if (next) {
          sel.value = next.value;
          triggerChange(sel);
          // Actualizar label visual del tema (cc-swatches muestra el valor activo aquí)
          var labelSpan = document.querySelector('[data-option-name="Metal"] .cc-swatches__label');
          if (labelSpan) labelSpan.textContent = next.value;
        }
      }
    }

    // 2. Elemento visual generado por Clickybox dentro del wrapper de Metal
    var metalWrapper = document.querySelector('[data-option-name="Metal"]');
    if (!metalWrapper) return;

    // Estrategia multi-intento: data-value, data-clickybox-value, texto visible
    var visualEl =
      metalWrapper.querySelector('[data-value="' + METAL_BLOQUEADO + '"]') ||
      metalWrapper.querySelector('[data-clickybox-value="' + METAL_BLOQUEADO + '"]') ||
      (function () {
        return Array.from(
          metalWrapper.querySelectorAll('li, label, .swatch-element, .clickybox-option, .option__btn, .cc-swatches__item')
        ).find(function (el) {
          return el.getAttribute('data-value') === METAL_BLOQUEADO ||
                 el.textContent.trim() === METAL_BLOQUEADO;
        });
      }());

    if (visualEl) {
      var clickable = visualEl.closest('li, .swatch-element, .cc-swatches__item') || visualEl;
      if (disabled) {
        clickable.classList.add('promesa-unavailable');
        clickable.setAttribute('aria-disabled', 'true');
      } else {
        clickable.classList.remove('promesa-unavailable');
        clickable.removeAttribute('aria-disabled');
      }
    }
  }

  // ═══════════════════════════════════════════════════════════════════
  // G. SEGMENTED CONTROL — reemplaza el pretty-select de Piedra
  // ═══════════════════════════════════════════════════════════════════

  function buildSegmentedControl() {
    var sel = getPiedraSelect();
    if (!sel) return;

    var nativeWrapper = sel.closest('.pretty-select');
    if (!nativeWrapper) return;

    var ctrl = document.createElement('div');
    ctrl.className = 'promesa-seg';
    ctrl.setAttribute('role', 'group');
    ctrl.setAttribute('aria-label', 'Tipo de piedra');

    Array.from(sel.options).forEach(function (opt) {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'promesa-seg__btn' + (opt.selected ? ' promesa-seg__btn--active' : '');
      btn.textContent = opt.value;
      btn.dataset.value = opt.value;

      btn.addEventListener('click', function () {
        if (sel.value === opt.value) return; // ya activo
        sel.value = opt.value;
        triggerChange(sel);
        ctrl.querySelectorAll('.promesa-seg__btn').forEach(function (b) {
          b.classList.toggle('promesa-seg__btn--active', b === btn);
        });
        onPiedraChange(opt.value);
      });

      ctrl.appendChild(btn);
    });

    // Ocultar el dropdown nativo de Piedra e insertar nuestro control en su lugar
    nativeWrapper.style.display = 'none';
    nativeWrapper.insertAdjacentElement('afterend', ctrl);

    // Ordenar los quilates numéricamente antes de aplicar restricciones
    // (Shopify los almacena en orden de creación, no numérico)
    sortQuilatesSelect();

    // Aplicar restricciones con el estado inicial
    onPiedraChange(sel.value || PIEDRA.MOI);
  }

  // ═══════════════════════════════════════════════════════════════════
  // G2. ORDENAR QUILATES NUMÉRICAMENTE EN EL SELECT
  //    Shopify no puede reordenar valores cuando hay variantes parciales
  //    (valores que solo existen para un tipo de piedra). Esta función
  //    reordena los <option> del select en el DOM para que el pretty-select
  //    los muestre en orden numérico correcto.
  // ═══════════════════════════════════════════════════════════════════

  function sortQuilatesSelect() {
    var sel = getQuilatesSelect();
    if (!sel) return;
    // parseFloat("0.7 (5.8mm)") → 0.7, parseFloat("3.5 (9.5mm)") → 3.5
    var opts = Array.from(sel.options);
    opts.sort(function (a, b) {
      return parseFloat(a.value) - parseFloat(b.value);
    });
    // Reinserta en orden correcto (mover un elemento existente lo mueve sin recrearlo)
    opts.forEach(function (o) { sel.appendChild(o); });
  }

  // ═══════════════════════════════════════════════════════════════════
  // H. HANDLER CENTRAL DE CAMBIO DE PIEDRA
  // ═══════════════════════════════════════════════════════════════════

  function onPiedraChange(piedra) {
    filterQuilates(piedra);
    setPlataDisabled(piedra === PIEDRA.DIA);
  }

  // ═══════════════════════════════════════════════════════════════════
  // I. DIAGNÓSTICO v2
  //    Ejecutar en consola del navegador: promesaDiscovery()
  // ═══════════════════════════════════════════════════════════════════

  window.promesaDiscovery = function () {
    console.group('%c🔍 Promesa — Diagnóstico v2', 'font-weight:bold;font-size:14px;');

    var checks = [
      { label: 'Piedra select',   el: getPiedraSelect() },
      { label: 'Quilates select', el: getQuilatesSelect() },
      { label: 'Metal select',    el: getMetalSelect() },
      { label: 'Metal wrapper',   el: document.querySelector('[data-option-name="Metal"]') },
    ];

    checks.forEach(function (c) {
      if (c.el) {
        console.log('%c✓ ' + c.label, 'color:green;font-weight:bold;', c.el);
        if (c.el.tagName === 'SELECT') {
          console.log('   Valores:', Array.from(c.el.options).map(function (o) { return '"' + o.value + '"'; }).join(', '));
        }
      } else {
        console.warn('✗ ' + c.label + ': NO encontrado');
      }
    });

    console.log('%cQuilates por piedra (dinámico):', 'font-weight:bold;', QUILATES_BY_PIEDRA);

    // Verificar elemento visual de Plata en Clickybox
    var mw = document.querySelector('[data-option-name="Metal"]');
    if (mw) {
      var plataEl = mw.querySelector('[data-value="' + METAL_BLOQUEADO + '"]');
      if (plataEl) {
        console.log('%c✓ Plata visual (data-value)', 'color:green;font-weight:bold;', plataEl);
      } else {
        console.warn('✗ Plata visual (data-value): no encontrado — usando fallback por texto');
        console.log('  Hijos directos del wrapper Metal:',
          Array.from(mw.children).map(function (c) {
            return c.tagName + (c.className ? '.' + c.className.trim().split(/\s+/).join('.') : '');
          }).join(', '));
      }
    }

    console.groupEnd();
  };

  // ═══════════════════════════════════════════════════════════════════
  // J. INICIALIZACIÓN
  // ═══════════════════════════════════════════════════════════════════

  function init() {
    if (!getPiedraSelect()) {
      setTimeout(init, 300);
      return;
    }
    // Deriva los quilates válidos por piedra desde las variantes reales
    // del producto antes de construir el control, para que el primer
    // render ya filtre correctamente (sin esperar a un cambio de piedra).
    fetchQuilatesPorPiedra(function (map) {
      QUILATES_BY_PIEDRA = map; // null si falló → filterQuilates usará el fallback
      buildSegmentedControl();
    });
  }

  document.readyState === 'loading'
    ? document.addEventListener('DOMContentLoaded', init)
    : init();

}());